import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { STAGE_LABELS } from "../lib/constants";
import { Flame, RefreshCw, PhoneCall, FileText, Receipt, Hourglass, ChevronRight, Loader2 } from "lucide-react";

const SLOTS = [
  ["recall_post_preventivo", "Recall post preventivo", PhoneCall],
  ["recall_generica", "Recall generica", PhoneCall],
  ["attesa_bolletta", "Attendiamo la bolletta", Receipt],
  ["attesa_proposta", "In attesa della proposta", Hourglass],
];
const fmt = (d) => (d ? new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "");
const ago = (d) => (d ? Math.max(0, Math.floor((Date.now() - new Date(d)) / 86400000)) : null);

function HotList({ slot, items, onClose }) {
  const navigate = useNavigate();
  const [key, label] = slot || [];
  return (
    <Dialog open={!!slot} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="hotpoint-dialog">
        <DialogHeader>
          <DialogTitle>{label}: {items.length}</DialogTitle>
          <DialogDescription>Individuati dall'AI leggendo le note delle azioni e dei preventivi del Preventivatore. In alto i lead fermi da più tempo.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-border border border-border rounded" data-testid={`hotpoint-list-${key}`}>
          {items.length === 0 && <p className="text-sm text-muted-foreground p-6 text-center">Nessun lead in questo stato.</p>}
          {items.map((l) => (
            <button key={l.id} type="button" onClick={() => navigate(l.kind === "preventivo" ? `/preventivatore?open=${l.id}` : `/leads/${l.id}`)} data-testid={`hotpoint-item-${l.id}`}
              className="w-full flex items-start justify-between gap-3 px-4 py-3 text-left hover:bg-secondary/50 transition-colors">
              <div className="min-w-0">
                <div className="font-semibold text-sm">{l.name}</div>
                <div className="text-[11px] text-muted-foreground">{l.kind === "preventivo" ? "Preventivatore" : STAGE_LABELS[l.stage] || l.stage}{l.assigned_to_name ? ` · ${l.assigned_to_name}` : ""}{l.phone ? ` · ${l.phone}` : ""}</div>
                {l.reason && <div className="text-xs mt-1 text-foreground/80 line-clamp-2"><FileText className="h-3 w-3 inline mr-1 text-primary" />{l.reason}</div>}
              </div>
              <div className="text-right shrink-0">
                <div className="text-[11px] text-muted-foreground">{fmt(l.note_at)}</div>
                {ago(l.note_at) !== null && <div className="text-xs font-semibold text-primary">{ago(l.note_at)} gg fa</div>}
                <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto mt-1" />
              </div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Hotpoints: stati operativi dei lead in pipeline dedotti dall'AI dalle note (aggiornati ogni ora).
export default function Hotpoints() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/dashboard/hotpoints").then((r) => setD(r.data)).catch(() => setD({ groups: {} })), []);
  useEffect(() => { load(); }, [load]);
  const refresh = async () => {
    setBusy(true);
    try {
      await api.post("/dashboard/hotpoints/refresh");
      toast.success("Analisi AI avviata: aggiornamento tra pochi secondi");
      setTimeout(() => { load(); setBusy(false); }, 12000);
    } catch { setBusy(false); toast.error("Impossibile avviare l'analisi"); }
  };
  const g = d?.groups || {};
  return (
    <Card className="p-5 rounded border-border flex flex-col" data-testid="hotpoints-section">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-bold flex items-center gap-1.5"><Flame className="h-4 w-4 text-primary" />Hotpoints</h2>
        <Button size="sm" variant="ghost" onClick={refresh} disabled={busy} data-testid="hotpoints-refresh" title="Aggiorna ora">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground mb-4" data-testid="hotpoints-last-run">
        AI sulle note dei lead e dei preventivi · {d?.last_run ? `aggiornato ${new Date(d.last_run).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}` : "in attesa della prima analisi"}
      </p>
      <div className="grid grid-cols-2 gap-3 flex-1">
        {SLOTS.map((s) => {
          const [k, label, Icon] = s, n = (g[k] || []).length;
          return (
            <button key={k} type="button" onClick={() => setOpen(s)} data-testid={`hotpoint-${k}`}
              className="text-left rounded border border-border p-3 hover:border-primary/60 transition-colors">
              <div className="flex items-center justify-between"><span className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground leading-tight">{label}</span><Icon className="h-3.5 w-3.5 text-primary shrink-0" /></div>
              <div className={`mt-2 text-3xl font-heading font-bold ${n ? "text-primary" : ""}`} data-testid={`hotpoint-${k}-count`}>{d ? n : "…"}</div>
            </button>
          );
        })}
      </div>
      <HotList slot={open} items={open ? g[open[0]] || [] : []} onClose={() => setOpen(null)} />
    </Card>
  );
}
